import { notifications } from "@/lib/notifications";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCountryCallingCode, isValidPhoneNumber } from "libphonenumber-js";
import { format, parse } from "date-fns";
import {
  IconUser,
  IconBriefcase,
  IconCalendar,
  IconMars,
  IconVenus,
  IconGenderBigender,
  IconBuildingStore,
  IconDeviceDesktop,
  IconCamera,
  IconFileText,
  IconPrinter,
  IconPencil,
  IconCode,
  IconGavel,
  IconDeviceMobile,
  IconDots,
  IconMapPin,
  IconPhone,
  IconClock,
  IconTags,
  IconCheck,
  IconArrowRight,
  IconArrowLeft,
  IconWorld,
  IconMap2,
  IconBuildingCommunity,
} from "@tabler/icons-react";
import { useAuthStore } from "../../../store/authStore";
import { useOfficeSpaceStore } from "../../../store/officeSpaceStore";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import Logo from "../../../components/common/Logo";
import SelectField from "../../../components/common/SelectField";
import GeoCombobox from "../../../components/common/GeoCombobox";
import CountrySelect from "../../../components/common/CountrySelect";
import ColorSchemeToggle from "../../../components/common/ColorSchemeToggle";
import LanguageSwitcher from "../../../components/common/LanguageSwitcher";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Calendar } from "../../../components/ui/calendar";
import TimePicker from "../../../components/common/TimePicker";
import bdDistricts from "../../../data/bd-districts.json";
import bdUpazilas from "../../../data/bd-upazilas.json";

// ── Constants ──────────────────────────────────────────────────────────────
// Suggestion chips carry both languages; the UI shows (and stores, since these are
// free-text) the label matching the active interface language.
const PREDEFINED_PROFESSIONS = [
  { bn: "কম্পিউটার অপারেটর", en: "Computer Operator" },
  { bn: "টাইপিস্ট", en: "Typist" },
  { bn: "ফটোগ্রাফার", en: "Photographer" },
  { bn: "গ্রাফিক ডিজাইনার", en: "Graphic Designer" },
  { bn: "ফ্রিল্যান্সার", en: "Freelancer" },
  { bn: "ব্যবসায়ী", en: "Businessman" },
  { bn: "ভিডিও এডিটর", en: "Video Editor" },
  { bn: "ডেটা এন্ট্রি অপারেটর", en: "Data Entry Operator" },
  { bn: "মোবাইল সার্ভিসিং", en: "Mobile Servicing" },
  { bn: "ল্যামিনেশন অপারেটর", en: "Lamination Operator" },
  { bn: "ফটোকপি অপারেটর", en: "Photocopy Operator" },
];

const PREDEFINED_SERVICES = [
  { bn: "ডকুমেন্ট প্রিন্ট", en: "Document Print" },
  { bn: "ফটোকপি", en: "Photocopy" },
  { bn: "ল্যামিনেশন", en: "Lamination" },
  { bn: "কম্পিউটার কম্পোজ", en: "Computer Compose" },
  { bn: "ছবি তোলা", en: "Photography" },
  { bn: "ভিসা প্রসেসিং", en: "Visa Processing" },
  { bn: "নোটারি সার্ভিস", en: "Notary Service" },
  { bn: "সাইবার ক্যাফে", en: "Cyber Café" },
  { bn: "মোবাইল রিচার্জ", en: "Mobile Recharge" },
  { bn: "ইন্টারনেট সার্ভিস", en: "Internet Service" },
  { bn: "ফটো এডিটিং", en: "Photo Editing" },
];

const BUSINESS_CATEGORIES = [
  { value: "cyber_cafe",       label: "সাইবার ক্যাফে",     labelEn: "Cyber Café",        icon: IconDeviceDesktop },
  { value: "photo_studio",     label: "ফটো স্টুডিও",       labelEn: "Photo Studio",      icon: IconCamera },
  { value: "document_service", label: "ডকুমেন্ট সার্ভিস", labelEn: "Document Service",  icon: IconFileText },
  { value: "print_shop",       label: "প্রিন্ট শপ",         labelEn: "Print Shop",        icon: IconPrinter },
  { value: "stationery",       label: "স্টেশনারি",          labelEn: "Stationery",        icon: IconPencil },
  { value: "it_service",       label: "আইটি সার্ভিস",      labelEn: "IT Service",        icon: IconCode },
  { value: "notary_legal",     label: "নোটারি / আইন",     labelEn: "Notary / Legal",    icon: IconGavel },
  { value: "mobile_service",   label: "মোবাইল শপ",         labelEn: "Mobile Shop",       icon: IconDeviceMobile },
  { value: "other",            label: "অন্যান্য",           labelEn: "Other",             icon: IconDots },
];

// Step meta drives both the left-panel hero and the form heading. Titles are pulled
// from i18n at render time (STEP_ICONS keeps the non-translatable icon refs).
const STEP_ICONS = [IconUser, IconBuildingStore];

// ── Helpers ────────────────────────────────────────────────────────────────
function Spinner({ size = 16 }) {
  return (
    <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

function SelectionCard({ icon: Icon, label, sub, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex w-full flex-col items-center gap-1.5 rounded-xl border p-2.5 text-center transition-colors ${
        selected
          ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-sm"
          : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-primary/40 hover:shadow-sm"
      }`}
    >
      {selected && (
        <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white">
          <IconCheck size={10} stroke={2.4} />
        </span>
      )}
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
        selected
          ? "bg-primary text-white"
          : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-300 group-hover:bg-primary/10 group-hover:text-primary"
      }`}>
        <Icon size={20} stroke={1.7} />
      </div>
      <div>
        <p className={`text-[13px] font-semibold leading-tight ${selected ? "text-primary" : "text-zinc-800 dark:text-zinc-100"}`}>
          {label}
        </p>
        {sub && <p className="mt-0.5 text-[11px] leading-tight text-zinc-400 dark:text-zinc-500">{sub}</p>}
      </div>
    </button>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const { user, updateProfile, getCurrentUser } = useAuthStore();
  const { officeSpaces, fetchOfficeSpaces, updateOfficeSpace } = useOfficeSpaceStore();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  // "en" | "bn" — picks the label language for the suggestion chips + category names.
  const lang = currentLanguage === "en" ? "en" : "bn";
  // Both-language predefined service labels, so a chip added in one language is still
  // recognised as "predefined" (not custom) after switching the interface language.
  const predefinedServiceLabels = PREDEFINED_SERVICES.flatMap(s => [s.bn, s.en]);

  // The Dokan a new owner sets up here is their own office space's business
  // profile — load it so we can both prefill and save to it.
  useEffect(() => {
    if (!officeSpaces || officeSpaces.length === 0) fetchOfficeSpaces().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Step 1
  const [profession, setProfession] = useState(user?.profession || "");
  const [gender, setGender]         = useState(user?.gender || null);
  const [dateOfBirth, setDateOfBirth] = useState(
    user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split("T")[0] : ""
  );
  const [address, setAddress]   = useState(user?.address || "");
  const [country, setCountry]   = useState(user?.country || "BD");
  const [district, setDistrict] = useState(user?.district || "");
  const [thana, setThana]       = useState(user?.thana || "");

  // On a fresh page load the store user may not be hydrated yet (or may be the minimal
  // post-register object) — pull the full profile so country/district/etc. are available.
  useEffect(() => {
    getCurrentUser?.().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill from the user record as it loads. The login response may arrive without
  // country/district (they hydrate a moment later via /me), so keep syncing country
  // until the user picks one themselves; fill the rest only while still empty so we
  // never clobber in-progress edits.
  const countryTouchedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (!countryTouchedRef.current && user.country) setCountry(user.country);
    setProfession((v) => v || user.profession || "");
    setGender((v) => v ?? (user.gender || null));
    setDateOfBirth((v) => v || (user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split("T")[0] : ""));
    setAddress((v) => v || user.address || "");
    setDistrict((v) => v || user.district || "");
    setThana((v) => v || user.thana || "");
  }, [user]);
  // Bangladesh → district/upazila dropdowns; other countries → free-text (driven by the
  // in-form country selector so switching it updates the fields live).
  const isBd = !country || country === "BD";

  // A Google sign-up has no phone yet (the backend stores a placeholder `g:<id>`): the number is required here, not verified (the
  // email already is), and Skip / Next stay locked until it is saved. The country is also open while the account has none.
  const needsPhone = String(user?.phone || "").startsWith("g:");
  const [phoneDigits, setPhoneDigits] = useState("");
  const dialCode = (() => { try { return country && country !== "BD" ? getCountryCallingCode(country) : "880"; } catch { return ""; } })();

  // Step 2
  const [shopName, setShopName]             = useState(user?.shopName || "");
  const [businessCategory, setBusinessCategory] = useState(user?.businessCategory || null);
  const [services, setServices]             = useState(user?.services ?? []);
  const [serviceInput, setServiceInput]     = useState("");
  const [businessAddress, setBusinessAddress] = useState(user?.businessAddress || "");
  const [openTime, setOpenTime]             = useState(user?.openTime || "09:00");
  const [closeTime, setCloseTime]           = useState(user?.closeTime || "18:00");
  const [showInDirectory, setShowInDirectory] = useState(!!user?.showInDirectory);
  const [errors, setErrors] = useState({});

  const inputCls = "w-full px-3.5 py-2.5 border border-zinc-300 dark:border-zinc-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 transition-shadow";
  const inputErrCls = "w-full px-3.5 py-2.5 border border-red-400 dark:border-red-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400/40 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 transition-shadow";
  const labelCls = "block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5";

  // Upazila options depend on the selected district (matched by its Bengali name).
  const selectedDistrict = bdDistricts.find((d) => d.bn_name === district || d.name === district);
  const upazilaOptions = selectedDistrict
    ? bdUpazilas.filter((u) => u.district_id === selectedDistrict.id).map((u) => ({ value: u.id, label: u.bn_name, en: u.name }))
    : [];

  const validateStep1 = () => {
    const e = {};
    if (needsPhone) {
      const full = phoneDigits ? (isBd ? phoneDigits : `+${dialCode}${phoneDigits}`) : "";
      const ok = isBd ? /^[0-9]{11}$/.test(full) : isValidPhoneNumber(full, country);
      if (!ok) e.phone = t("onboarding.phoneRequired");
    }
    if (!profession.trim()) e.profession = t("onboarding.professionRequired");
    if (!gender) e.gender = t("onboarding.genderRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!shopName.trim()) e.shopName = t("onboarding.shopNameRequired");
    if (!businessCategory) e.businessCategory = t("onboarding.categoryRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const finish = async (partial = false) => {
    setLoading(true);
    try {
      // Personal fields live on the user profile.
      const payload = {
        isOnboarded: true,
        ...(partial ? {} : {
          country: country || null,
          profession: profession.trim() || null,
          gender: gender || null,
          dateOfBirth: dateOfBirth || null,
          address: address.trim() || null,
          // Never overwrite an existing district/thana with null just because the
          // local field is empty (e.g. not hydrated from the user record yet) —
          // omit them so the backend keeps the value saved at registration.
          ...(district.trim() ? { district: district.trim() } : {}),
          ...(thana.trim() ? { thana: thana.trim() } : {}),
        }),
      };
      await updateProfile(payload);

      // Shop / business ("Dokan") fields now live on the owner's OfficeSpace, not
      // on the user profile — save them to the default office the user owns.
      if (!partial) {
        let offices = officeSpaces;
        if (!offices || offices.length === 0) {
          offices = await fetchOfficeSpaces().catch(() => []);
        }
        const owned = (offices || []).filter((o) => o.ownerId === user?.id);
        const target = owned.find((o) => o.isDefault) || owned[0];
        if (target) {
          await updateOfficeSpace(target.id, {
            name: shopName.trim() || target.name, // office name == shop name
            businessCategory: businessCategory || null,
            services,
            businessAddress: businessAddress.trim() || null,
            openTime: openTime || null,
            closeTime: closeTime || null,
            showInDirectory,
          });
        }
      }
    } catch (err) {
      console.error("Onboarding save error:", err);
    } finally {
      // Always force isOnboarded=true locally AFTER any API response has been
      // written to the store, so the PrivateLayout gate never redirects back.
      useAuthStore.setState(prev => ({ user: { ...prev.user, isOnboarded: true } }));
      setLoading(false);
      navigate("/");
    }
  };

  const goToStep = async (n) => {
    if (animating) return;
    if (n > step && step === 1 && !validateStep1()) return;
    if (n > step && step === 1 && needsPhone) {
      // Save the phone (and the country, if the Google account came without one) before moving on. Not verified.
      const full = isBd ? phoneDigits : `+${dialCode}${phoneDigits}`;
      try {
        setLoading(true);
        await updateProfile({ phone: full, country: country || "BD" });
      } catch (err) {
        setErrors({ phone: /taken|exist/i.test(String(err?.message)) ? t("onboarding.phoneTaken") : (err?.message || t("onboarding.phoneRequired")) });
        return;
      } finally {
        setLoading(false);
      }
    }
    setErrors({});
    setAnimating(true);
    setTimeout(() => {
      setStep(n);
      setAnimating(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 150);
  };

  const handleFinish = () => {
    if (!validateStep2()) return;
    finish();
  };

  const addService = (s) => { if (s && !services.includes(s)) setServices(p => [...p, s]); };
  const removeService = (s) => setServices(p => p.filter(x => x !== s));

  // Translatable step meta (title/subtitle/desc) keyed off the step index.
  const STEP_META = [
    { title: t("onboarding.step1Title"), subtitle: t("onboarding.step1Subtitle"), desc: t("onboarding.step1Desc"), icon: STEP_ICONS[0] },
    { title: t("onboarding.step2Title"), subtitle: t("onboarding.step2Subtitle"), desc: t("onboarding.step2Desc"), icon: STEP_ICONS[1] },
  ];
  const totalSteps = STEP_META.length;
  const currentStep = STEP_META[step - 1];
  const StepIcon = currentStep.icon;

  // ── Left panel ────────────────────────────────────────────────────────────
  const leftPanel = (
    <div className="hidden lg:flex flex-col justify-between h-full px-10 py-10">
      <div className="flex items-center justify-between">
        <img src={lang === "en" ? "/images/logo-en.svg" : "/images/logo.svg"} alt="SohozKaj" width={130} className="block h-auto" />
        <div className="flex items-center gap-2">
          <div className="[&_button]:border-white/30 [&_button]:bg-white/10 [&_button]:text-white/80 [&_.bg-primary]:bg-white/90 [&_.bg-primary]:text-primary">
            <LanguageSwitcher />
          </div>
          <div className="[&_button]:bg-white/15 [&_button]:hover:bg-white/25 [&_button]:text-white [&_button]:border-white/20">
            <ColorSchemeToggle size="sm" />
          </div>
        </div>
      </div>

      {/* Middle content */}
      <div>
        {/* Step dots */}
        <div className="flex items-center gap-3 mb-8">
          {STEP_META.map((s, i) => {
            const n = i + 1;
            const done = n < step;
            const active = n === step;
            return (
              <div key={n} className="flex items-center gap-3">
                <div className={`flex items-center justify-center rounded-full font-bold text-sm transition-all duration-300 ${
                  done    ? "w-8 h-8 bg-white/30 text-white" :
                  active  ? "w-10 h-10 bg-white text-primary shadow-lg shadow-black/10" :
                            "w-8 h-8 bg-white/15 text-white/50"
                }`}>
                  {done ? <IconCheck size={15} /> : n}
                </div>
                {i < totalSteps - 1 && (
                  <div className={`h-px w-12 transition-all duration-500 ${done ? "bg-white/60" : "bg-white/20"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step info */}
        <div className={`transition-all duration-300 ${animating ? "opacity-0 translate-y-2" : "opacity-100"}`}>
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5">
            <StepIcon size={28} className="text-white" stroke={1.7} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white leading-tight mb-3">
            {currentStep.title}
          </h1>
          <p className="text-base text-white/70 leading-relaxed max-w-xs">
            {currentStep.desc}
          </p>
        </div>

        {/* Feature list */}
        <div className="mt-10 flex flex-col gap-4">
          {[
            { text: t("onboarding.feature1"), sub: t("onboarding.feature1Sub") },
            { text: t("onboarding.feature2"), sub: t("onboarding.feature2Sub") },
            { text: t("onboarding.feature3"), sub: t("onboarding.feature3Sub") },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconCheck size={11} className="text-white" stroke={2.4} />
              </div>
              <div>
                <span className="text-sm font-medium text-white/90 leading-snug">{item.text}</span>
                <p className="text-xs text-white/85 mt-0.5 leading-relaxed">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom: step counter only */}
      <div className="flex items-center">
        <p className="text-sm text-white/40">{t("onboarding.stepCounter", { step, total: totalSteps })}</p>
      </div>
    </div>
  );

  // ── Step 1 form ───────────────────────────────────────────────────────────
  const step1Form = (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>{t("auth.country")} <span className="text-red-400">*</span></label>
          {/* Country is chosen (and required) during registration — it must stay
              locked here so users can't re-edit it on the onboarding step. */}
          <CountrySelect
            value={country}
            disabled={!needsPhone}
            onChange={(code) => { countryTouchedRef.current = true; setCountry(code); setDistrict(""); setThana(""); }}
          />
        </div>
        <div>
          <label className={labelCls}>{t("auth.phoneNumber")} <span className="text-red-400">*</span></label>
          <div className="relative">
            {!(needsPhone && !isBd) && <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"><IconPhone size={16} stroke={1.7} /></span>}
            {needsPhone ? (
              <div className="flex">
                {!isBd && <span className="flex items-center rounded-l-lg border border-r-0 border-zinc-300 bg-zinc-50 px-3 text-sm text-zinc-500 dark:border-zinc-600 dark:bg-zinc-800">+{dialCode}</span>}
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phoneDigits}
                  maxLength={15}
                  onChange={(e) => { setPhoneDigits(e.target.value.replace(/\D/g, "")); if (errors.phone) setErrors((p) => ({ ...p, phone: undefined })); }}
                  placeholder={t("auth.phonePlaceholder")}
                  className={`${errors.phone ? inputErrCls : inputCls} ${isBd ? "pl-10" : "rounded-l-none"}`}
                />
              </div>
            ) : (
              <input type="text" value={user?.phone || ""} disabled className={`${inputCls} pl-10 opacity-70 cursor-not-allowed`} />
            )}
          </div>
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
        </div>
      </div>

      <div>
        <label className={labelCls}>
          {t("profile.profession")} <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"><IconBriefcase size={16} stroke={1.7} /></span>
          <input
            value={profession}
            onChange={e => { setProfession(e.target.value); setErrors(p => ({ ...p, profession: undefined })); }}
            placeholder={t("onboarding.professionPlaceholder")}
            className={`${errors.profession ? inputErrCls : inputCls} pl-10`}
          />
        </div>
        {errors.profession && <p className="text-xs text-red-500 mt-1">{errors.profession}</p>}
        {/* Suggested profession badges */}
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {PREDEFINED_PROFESSIONS.map(p => {
            const label = p[lang];
            return (
              <button key={p.en} type="button"
                onClick={() => { setProfession(label); setErrors(prev => ({ ...prev, profession: undefined })); }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  profession === label
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600 hover:border-primary hover:text-primary"
                }`}
              >{label}</button>
            );
          })}
        </div>
      </div>

      <div>
        <label className={labelCls}>
          {t("profile.gender")} <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {[
            { value: "male",   label: t("profile.male"),   icon: IconMars },
            { value: "female", label: t("profile.female"), icon: IconVenus },
            { value: "other",  label: t("profile.other"),  icon: IconGenderBigender },
          ].map(opt => (
            <SelectionCard key={opt.value} icon={opt.icon} label={opt.label}
              selected={gender === opt.value}
              onClick={() => { setGender(g => g === opt.value ? null : opt.value); setErrors(p => ({ ...p, gender: undefined })); }}
            />
          ))}
        </div>
        {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
      </div>

      <div>
        <label className={labelCls}>{t("profile.dateOfBirth")}</label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className={`${inputCls} flex items-center gap-2.5 text-left`}>
              <IconCalendar size={16} className="text-zinc-400 flex-shrink-0" stroke={1.7} />
              <span className={dateOfBirth ? "text-zinc-800 dark:text-zinc-100" : "text-zinc-400 dark:text-zinc-500"}>
                {dateOfBirth
                  ? format(parse(dateOfBirth, "yyyy-MM-dd", new Date()), "dd MMMM yyyy")
                  : t("onboarding.selectDob")}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={dateOfBirth ? parse(dateOfBirth, "yyyy-MM-dd", new Date()) : undefined}
              onSelect={date => setDateOfBirth(date ? format(date, "yyyy-MM-dd") : "")}
              fromYear={1950}
              toYear={new Date().getFullYear() - 5}
              defaultMonth={dateOfBirth ? parse(dateOfBirth, "yyyy-MM-dd", new Date()) : new Date(1990, 0)}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <label className={labelCls}>{t("profile.address") || "ঠিকানা"}</label>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"><IconMapPin size={16} stroke={1.7} /></span>
          <input value={address} onChange={e => setAddress(e.target.value)} placeholder={t("onboarding.addressPlaceholder")} className={`${inputCls} pl-10`} />
        </div>
      </div>

      {/* District + Upazila — BD gets the geo dropdowns; other countries type free text
          (the BD district/upazila lists don't apply abroad). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            <span className="flex items-center gap-1.5"><IconMap2 size={14} stroke={1.7} className="text-zinc-400" /> {t("onboarding.district")}</span>
          </label>
          {isBd ? (
            <GeoCombobox
              value={district}
              onChange={(v) => { setDistrict(v); setThana(""); }}
              options={bdDistricts.map((d) => ({ value: d.id, label: d.bn_name, en: d.name }))}
              placeholder={t("onboarding.selectDistrict")}
              selectOnly
            />
          ) : (
            <input
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder={t("onboarding.district")}
              className={inputCls}
            />
          )}
        </div>
        <div>
          <label className={labelCls}>
            <span className="flex items-center gap-1.5"><IconBuildingCommunity size={14} stroke={1.7} className="text-zinc-400" /> {t("onboarding.upazila")}</span>
          </label>
          {isBd ? (
            <GeoCombobox
              value={thana}
              onChange={setThana}
              options={upazilaOptions}
              placeholder={district ? t("onboarding.selectUpazila") : t("onboarding.selectDistrictFirst")}
              disabled={!district}
              selectOnly
            />
          ) : (
            <input
              value={thana}
              onChange={(e) => setThana(e.target.value)}
              placeholder={t("onboarding.upazila")}
              className={inputCls}
            />
          )}
        </div>
      </div>
    </div>
  );

  // ── Step 2 form ───────────────────────────────────────────────────────────
  const step2Form = (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            {t("profile.shopName")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"><IconBuildingStore size={16} stroke={1.7} /></span>
            <input
              value={shopName}
              onChange={e => { setShopName(e.target.value); setErrors(p => ({ ...p, shopName: undefined })); }}
              placeholder={t("onboarding.shopNamePlaceholder")}
              className={`${errors.shopName ? inputErrCls : inputCls} pl-10`}
            />
          </div>
          {errors.shopName && <p className="text-xs text-red-500 mt-1">{errors.shopName}</p>}
        </div>
        <div>
          <label className={labelCls}>
            {t("profile.businessCategory")} <span className="text-red-400">*</span>
          </label>
          <SelectField
            value={businessCategory ?? ""}
            onChange={val => { setBusinessCategory(val || null); setErrors(p => ({ ...p, businessCategory: undefined })); }}
            placeholder={t("profile.selectCategory")}
            triggerClassName={`${errors.businessCategory ? inputErrCls : inputCls}`}
            options={[
              { value: "",                label: t("profile.selectCategory") },
              ...BUSINESS_CATEGORIES.map(c => ({ value: c.value, label: lang === "en" ? c.labelEn : c.label })),
            ]}
          />
          {errors.businessCategory && <p className="text-xs text-red-500 mt-1">{errors.businessCategory}</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 p-4 flex flex-col gap-3">
        <div>
          <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 flex items-center gap-1.5">
            <IconTags size={15} className="text-primary" stroke={1.7} /> {t("onboarding.yourServices")}
          </p>
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">{t("onboarding.servicesHelp")}</p>
        </div>

        {/* Suggested badges */}
        <div>
          <p className="text-xs font-medium text-zinc-400 dark:text-zinc-500 mb-1.5">{t("onboarding.suggestedServices")}</p>
          <div className="flex flex-wrap gap-1.5">
            {PREDEFINED_SERVICES.map(s => {
              const label = s[lang];
              const active = services.includes(label);
              return (
                <button key={s.en} type="button"
                  onClick={() => active ? removeService(label) : addService(label)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? "bg-primary text-white border-primary"
                      : "bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600 hover:border-primary hover:text-primary"
                  }`}
                >{label}</button>
              );
            })}
          </div>
        </div>

        {/* Selected custom services */}
        {services.filter(s => !predefinedServiceLabels.includes(s)).length > 0 && (
          <div>
            <p className="text-xs font-medium text-zinc-400 dark:text-zinc-500 mb-1.5">{t("onboarding.yourAddedServices")}</p>
            <div className="flex flex-wrap gap-1.5">
              {services.filter(s => !predefinedServiceLabels.includes(s)).map(s => (
                <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                  {s}
                  <button type="button" onClick={() => removeService(s)} className="ml-0.5 font-bold text-primary/60 hover:text-red-500">×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Custom input */}
        <div className="flex gap-2 pt-1 border-t border-zinc-200 dark:border-zinc-700">
          <input value={serviceInput} onChange={e => setServiceInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addService(serviceInput.trim()); setServiceInput(""); }}}
            placeholder={t("onboarding.customServicePlaceholder")}
            className={`${inputCls} flex-1 text-sm`}
          />
          <button type="button" onClick={() => { addService(serviceInput.trim()); setServiceInput(""); }}
            className="px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-bold rounded-lg transition-colors">+</button>
        </div>
      </div>

      <div>
        <label className={labelCls}>{t("onboarding.businessAddress")}</label>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"><IconMapPin size={16} stroke={1.7} /></span>
          <input value={businessAddress} onChange={e => setBusinessAddress(e.target.value)} placeholder={t("onboarding.businessAddressPlaceholder")} className={`${inputCls} pl-10`} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            <span className="flex items-center gap-1.5"><IconClock size={13} stroke={1.7} /> {t("onboarding.openTime")}</span>
          </label>
          <TimePicker value={openTime} onChange={setOpenTime} />
        </div>
        <div>
          <label className={labelCls}>
            <span className="flex items-center gap-1.5"><IconClock size={13} stroke={1.7} /> {t("onboarding.closeTime")}</span>
          </label>
          <TimePicker value={closeTime} onChange={setCloseTime} />
        </div>
      </div>

      {/* Directory toggle card */}
      <button type="button" onClick={() => setShowInDirectory(v => !v)}
        className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
          showInDirectory
            ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-sm"
            : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-primary/40"
        }`}
      >
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${showInDirectory ? "bg-primary text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"}`}>
          <IconWorld size={18} stroke={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${showInDirectory ? "text-primary" : "text-zinc-800 dark:text-zinc-100"}`}>
            {t("onboarding.directoryTitle")}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
            {t("onboarding.directoryDesc")}
          </p>
        </div>
        <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${showInDirectory ? "bg-primary border-primary" : "border-zinc-300 dark:border-zinc-600"}`}>
          {showInDirectory && <IconCheck size={12} className="text-white" stroke={2.4} />}
        </div>
      </button>
    </div>
  );

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex overflow-hidden bg-zinc-50 dark:bg-zinc-950 font-hind">

      {/* Floating skip button */}
      <button
        type="button"
        aria-disabled={needsPhone}
        title={needsPhone ? t("onboarding.phoneRequired") : undefined}
        onClick={() => {
          // A disabled button cannot explain itself, so it only LOOKS disabled: the click says what is missing.
          if (needsPhone) {
            notifications.show({ message: t("onboarding.phoneRequired"), color: "red" });
            setErrors((p) => ({ ...p, phone: t("onboarding.phoneRequired") }));
            return;
          }
          if (step === 1) {
            setErrors({});
            setAnimating(true);
            setTimeout(() => { setStep(2); setAnimating(false); window.scrollTo({ top: 0, behavior: "smooth" }); }, 150);
          } else {
            finish(true);
          }
        }}
        className="fixed top-4 right-4 z-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-40 px-3 py-1.5 text-xs font-medium text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
      >
        {t("onboarding.skip")} →
      </button>

      {/* ── Left brand panel ── */}
      <div className="hidden lg:flex lg:w-[420px] xl:w-[480px] flex-shrink-0 relative overflow-hidden bg-gradient-to-br from-[#d4780a] via-[#b35d00] to-[#8a4800]">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative z-10 w-full">
          {leftPanel}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col h-screen overflow-y-auto">

        {/* Top bar — mobile only (logo + step dots) */}
        <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <Logo w={110} />
          <div className="flex items-center gap-2">
            {STEP_META.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i + 1 === step ? "w-6 bg-primary" : i + 1 < step ? "w-4 bg-primary/50" : "w-4 bg-zinc-200 dark:bg-zinc-700"}`} />
            ))}
          </div>
        </div>

        {/* Form area */}
        <div className="flex-1 flex flex-col px-5 sm:px-8 lg:px-10 py-6">
          <div className="w-full max-w-2xl mx-auto flex flex-col flex-1">

            {/* Step heading */}
            <div className={`mb-7 transition-all duration-300 ${animating ? "opacity-0 -translate-y-2" : "opacity-100"}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-primary uppercase tracking-widest">{t("onboarding.stepCounter", { step, total: totalSteps })}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 leading-tight">{currentStep.title}</h2>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1.5 text-sm leading-relaxed">{currentStep.subtitle}</p>
            </div>

            {/* Form content */}
            <div className={`flex-1 transition-all duration-300 ${animating ? "opacity-0 translate-y-4" : "opacity-100"}`}>
              {step === 1 ? step1Form : step2Form}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-zinc-100 dark:border-zinc-800">
              <div>
                {step === 2 ? (
                  <button type="button" onClick={() => goToStep(1)}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-[13px] font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                    <IconArrowLeft size={15} stroke={1.8} /> {t("onboarding.back")}
                  </button>
                ) : <div />}
              </div>

              <button type="button"
                onClick={step === 1 ? () => goToStep(2) : handleFinish}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-[13px] font-semibold rounded-lg transition-colors active:scale-95">
                {loading ? <Spinner size={15} /> : null}
                {step === 1 ? t("onboarding.nextStep") : t("onboarding.finish")}
                {!loading && <IconArrowRight size={15} stroke={1.8} />}
              </button>
            </div>

          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-xs text-zinc-300 dark:text-zinc-700 pb-6 px-6">
          {t("onboarding.bottomNote")}
        </p>
      </div>
    </div>
  );
}
